import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { validateAddressForm, ADDRESS_REQUIRED_FIELDS } from './account.logic';

/**
 * 住所フォームバリデーションコンポーネント
 *
 * @element kd-address-form-validate
 * @description フォーム送信時にバリデーションを実行し、エラー表示を制御する。
 *              jQuery バリデーション → native DOM に置換。
 *
 * @attr {string} form-id - 対象フォームのID（デフォルト: address_form_new）
 */
@customElement('kd-address-form-validate')
export class KdAddressFormValidate extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String, attribute: 'form-id' })
  formId = 'address_form_new';

  /**
   * 選択肢が 0 件の `<select>` は利用者が値を入れられないため必須判定から外す。
   *
   * 都道府県を持たない国（シンガポール等）を選ぶと Shopify.CountryProvinceSelector が
   * 都道府県 select の option を空にし、kd-address-form-toggle がコンテナを隠す。
   * この状態で `address[province]` を必須にすると、入力不能なフィールドを理由に
   * 送信が永久にブロックされ、しかもエラー文も隠れたコンテナの中で見えない。
   */
  private applicableFields(form: HTMLFormElement): typeof ADDRESS_REQUIRED_FIELDS {
    return ADDRESS_REQUIRED_FIELDS.filter((field) => {
      const el = form.querySelector(`[name="${field.name}"]`);
      return !(el instanceof HTMLSelectElement) || el.options.length > 0;
    });
  }

  private handleSubmit = (e: Event): void => {
    const form = e.target as HTMLFormElement;
    const requiredFields = this.applicableFields(form);
    const formData: Record<string, string> = {};

    for (const field of requiredFields) {
      const input = form.querySelector(`[name="${field.name}"]`) as HTMLInputElement | HTMLSelectElement | null;
      formData[field.name] = input?.value ?? '';
    }

    const result = validateAddressForm(formData, requiredFields);

    // エラー表示を更新
    //
    // 入力欄自体の色付けは行わない。base.css の `.customer .field input`（詳細度 0,2,1）が
    // `background-color: transparent` を当てているため、ユーティリティクラス（0,1,0）では
    // 勝てず、!important を使わずに実現できない。v2 は他の箇所（v2-cart-footer /
    // v2-product-warning 等）でもエラーを文字色のみで示す方針に揃えている。
    for (const field of ADDRESS_REQUIRED_FIELDS) {
      const errorEl = form.querySelector(`#${field.errorId}`) ?? document.getElementById(field.errorId);

      const hasError = result.errors.includes(field.name);

      if (errorEl) {
        (errorEl as HTMLElement).classList.toggle('hidden', !hasError);
      }
    }

    if (!result.isValid) {
      e.preventDefault();
    }
  };

  connectedCallback(): void {
    super.connectedCallback();

    const form = this.querySelector(`#${this.formId}`) ?? document.getElementById(this.formId);
    if (form) {
      form.addEventListener('submit', this.handleSubmit);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    const form = this.querySelector(`#${this.formId}`) ?? document.getElementById(this.formId);
    if (form) {
      form.removeEventListener('submit', this.handleSubmit);
    }
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-address-form-validate': KdAddressFormValidate;
  }
}
